import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../../contexts/GlobalContext";
import { array, boolean, object, setLocale, string } from "yup";
import { en } from "yup-locales";
import { _ROUTES } from "../../../utils/_routes_constants";
import {
  _ACTIONS,
  _COLORS,
  _HTTP,
  _MEMBER,
  _SWAL_ICONS,
} from "../../../utils/constants";
import { useFormik } from "formik";
import { RequestHelper } from "../../../utils/requestHelper";
import { getMuiTableConfig } from "../../../utils/MuiTableUtils/muiTableBuilder";
import { CompaniesListColumns } from "../../../utils/MuiTableUtils/ColumnBuilders/CompaniesListColumns";
import { useMaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import BasicModal from "../../helpers/BasicModal";
import BusinessIcon from "@mui/icons-material/Business";
import { BasicFormikForm } from "../../form/BasicFormikForm";
import { BasicListContext } from "../../../contexts/BasicListContext";
import { MuiTableComponent } from "../../helpers/MuiTableComponent";
import { buildOptions, getEntityProperties } from "../../../utils/formHelper";
import _ from "lodash";
import { createToast } from "../../../utils/toast";

export const CompaniesList = ({
  editData,
  setEditData,
  showModal,
  setShowModal,
}) => {
  const { open } = useContext(GlobalContext);
  const [options, setOptions] = useState({});
  const v = object({
    name: string().required().label("Company Name"),
    code: string().required().label("Code"),
    sites: array().label("Sites"),
    active: boolean().required().label("Actif"),
  });

  setLocale(en);

  const formik = useFormik({
    initialValues: {
      name: "",
      code: "",
      sites: [],
      active: true,
    },
    onSubmit: (values) => {
      const methode = editData ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = _ROUTES.COMPANIES + (editData ? `/${editData.id}` : "");

      const sites = getEntityProperties(values.sites, "@id");

      const company = {
        name: values.name,
        code: values.code,
        sites: sites,
        active: values.active,
      };

      const rq = new RequestHelper();

      rq.doRequest(methode, url, company).then((res) => {
        if (res.status === _HTTP.created || res.status === _HTTP.ok) {
          createToast(_SWAL_ICONS.success, "Company saved successfully");
          setShowModal(false);
          setEditData(null);
          setRefresh((prev) => {
            return prev + 1;
          });
        } else {
          createToast(_SWAL_ICONS.error, "Error saving company");
        }
      });
    },
    validationSchema: v,
  });

  const {
    rowCount,
    data,
    isLoaded,
    isError,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    isLoading,
    setRefresh,
  } = useContext(BasicListContext);

  // Get the table configuration
  const c = getMuiTableConfig(
    // Params
    { enableColumnFilters: true, enableExpanding: true },
    // States & setStates (from context) Have to be the same as this
    {
      rowCount,
      isLoaded,
      isError,
      pagination,
      setPagination,
      columnFilters,
      setColumnFilters,
      globalFilter,
      setGlobalFilter,
      sorting,
      setSorting,
      isLoading,
    }
  );

  const columns = CompaniesListColumns({
    setEditData,
    setShowModal,
    setRefresh,
  });

  const table = useMaterialReactTable({
    columns: columns,
    data: data,
    ...c,
    enableColumnResizing: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    initialState: {
      density: "compact",
    },
  });

  useEffect(() => {
    if (editData) {
      formik.setValues((prev) => ({
        name: editData.name,
        code: editData.code,
        sites: getEntityProperties(editData?.sites, "@id"),
        active: editData.active,
      }));
    } else {
      formik.resetForm();
    }
  }, [editData]);

  useEffect(() => {
    if (_.isEmpty(options)) {
      const rq = new RequestHelper();
      rq.doRequest(_ACTIONS.GET, _ROUTES.SITES, {
        pagination: false,
      }).then((res) => {
        if (res.status === _HTTP.ok) {
          setOptions({ sites: buildOptions(res.data[_MEMBER], "name", "@id") });
        }
      });
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: open ? window.innerWidth - 340 : window.innerWidth - 64,
          borderRadius: "1px",
          boxShadow: `0px 0px 2px ${_COLORS.grey} `,
          overflowX: "auto",
        }}>
        {/* Table Component is here */}
        <MuiTableComponent route={_ROUTES.COMPANIES} table={table} />
      </Box>
      {/* Modal  */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={"Company Form"}
        content={
          <BasicFormikForm
            formik={formik}
            setEditData={setEditData}
            setShowModal={setShowModal}
            validations={v}
            options={options}
          />
        }
        icon={<BusinessIcon />}
        width={"500"}
      />
    </>
  );
};

export default CompaniesList;
